/* Durnev — horizontal scroller for the collection-bar category strip.
   Adds prev/next arrows that scroll the .durnev-coll-bar__cats container
   by a fixed amount. Arrows auto-hide when there's no horizontal overflow
   (i.e. on wide enough viewports) and disable when at the start/end.

   Markup expected:
     <durnev-cats-scroller class="durnev-coll-bar__cats-wrap">
       <button .durnev-coll-bar__cats-arrow--prev>
       <nav .durnev-coll-bar__cats>
       <button .durnev-coll-bar__cats-arrow--next>
     </durnev-cats-scroller>
*/
class DurnevCatsScroller extends HTMLElement {
  connectedCallback() {
    this.cats = this.querySelector('.durnev-coll-bar__cats');
    this.prev = this.querySelector('.durnev-coll-bar__cats-arrow--prev');
    this.next = this.querySelector('.durnev-coll-bar__cats-arrow--next');
    if (!this.cats || !this.prev || !this.next) return;

    this.SCROLL_AMOUNT = 200; // px per click

    this.prev.addEventListener('click', () => {
      this.cats.scrollBy({ left: -this.SCROLL_AMOUNT, behavior: 'smooth' });
    });
    this.next.addEventListener('click', () => {
      this.cats.scrollBy({ left: this.SCROLL_AMOUNT, behavior: 'smooth' });
    });

    // Sync arrow state with scroll position
    this.cats.addEventListener('scroll', () => this.update(), { passive: true });

    // Detect overflow changes (resize, font-load reflow)
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.update());
      this.ro.observe(this.cats);
    } else {
      window.addEventListener('resize', () => this.update());
    }

    // Initial state — give layout a tick to settle, then centre the active
    // category if it exists so the user immediately sees where they are.
    requestAnimationFrame(() => {
      this.update();
      const active = this.cats.querySelector('.durnev-coll-bar__cat.is-active');
      if (active) {
        const max = this.cats.scrollWidth - this.cats.clientWidth;
        if (max > 1) {
          // Centre active item: scroll so its midpoint is in container midpoint.
          const target = active.offsetLeft - (this.cats.clientWidth - active.offsetWidth) / 2;
          this.cats.scrollLeft = Math.max(0, Math.min(max, target));
          this.update();
        }
      }
    });
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
  }

  update() {
    const max = this.cats.scrollWidth - this.cats.clientWidth;
    const hasOverflow = max > 1;
    // Hide arrows entirely when nothing to scroll — avoids visual clutter
    // on wide viewports where all categories already fit.
    this.prev.hidden = !hasOverflow;
    this.next.hidden = !hasOverflow;
    if (!hasOverflow) return;
    this.prev.disabled = this.cats.scrollLeft <= 0;
    this.next.disabled = this.cats.scrollLeft >= max - 1;
  }
}
customElements.define('durnev-cats-scroller', DurnevCatsScroller);
